import React, { useEffect, useState } from "react";
import { Plus, Trash2, PauseCircle, PlayCircle, RefreshCw, ShieldCheck, Loader2 } from "lucide-react";
import { http, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const EMPTY_FORM = { name: "", email: "", password: "", role: "assistant", clinic_id: "" };

const when = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const initials = (name) =>
  (name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

const CONFIRM_COPY = {
  suspend: {
    title: "Pause this account?",
    body: (u) => `${u.name} (${u.email}) will be signed out immediately and blocked from logging back in until you reactivate them.`,
    action: "Pause account",
    actionClass: "bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-400",
  },
  delete: {
    title: "Delete this account permanently?",
    body: (u) => `${u.name} (${u.email}) and their login will be permanently removed. This cannot be undone.`,
    action: "Delete account",
    actionClass: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500",
  },
};

const IconAction = ({ tone, title, disabled, onClick, children }) => {
  const tones = {
    amber: "border-amber-200 text-amber-600 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700",
    emerald: "border-emerald-200 text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700",
    red: "border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700",
  };
  return (
    <button type="button" title={title} disabled={disabled} onClick={onClick}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-white transition-colors disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300 disabled:hover:bg-white ${tones[tone]}`}>
      {children}
    </button>
  );
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [confirm, setConfirm] = useState(null); // { user, action: "suspend" | "delete" }
  const [working, setWorking] = useState(false);

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await http.get("/admin/users");
      setUsers(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) return toast.error("Name, email and password are required");
    setCreating(true);
    try {
      await http.post("/admin/users", { ...form, clinic_id: form.clinic_id.trim() || null });
      setForm(EMPTY_FORM);
      toast.success("Account created");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCreating(false);
    }
  };

  const reactivate = async (u) => {
    try {
      await http.patch(`/admin/users/${u.id}/status`, { status: "active" });
      toast.success("Account reactivated");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      if (confirm.action === "suspend") {
        await http.patch(`/admin/users/${confirm.user.id}/status`, { status: "suspended" });
        toast.success("Account paused");
      } else {
        await http.delete(`/admin/users/${confirm.user.id}`);
        toast.success("Account deleted");
      }
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
      setConfirm(null);
    } finally {
      setWorking(false);
    }
  };

  const copy = confirm ? CONFIRM_COPY[confirm.action] : null;

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-xs">User management · super admin</p>
          <h1 className="mt-2 font-head text-4xl font-light tracking-tight text-slate-900">
            Manage <span className="font-extrabold">accounts</span>
          </h1>
        </div>
        <button data-testid="admin-users-refresh" onClick={load} disabled={busy} className="dm-btn-ghost">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {error && <p data-testid="admin-users-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}

      <form onSubmit={createUser} className="dm-panel p-5 md:p-6" data-testid="admin-user-form">
        <h2 className="font-head text-lg font-semibold text-slate-800">Create an account</h2>
        <p className="mt-1 text-sm text-slate-500">Owner accounts run a clinic; assistant accounts need a clinic to be useful.</p>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <div>
            <p className="label-xs mb-1.5">Name</p>
            <input data-testid="admin-user-name-input" className="dm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <p className="label-xs mb-1.5">Email</p>
            <input type="email" data-testid="admin-user-email-input" className="dm-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <p className="label-xs mb-1.5">Password</p>
            <input type="password" data-testid="admin-user-password-input" className="dm-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div>
            <p className="label-xs mb-1.5">Role</p>
            <select data-testid="admin-user-role-select" className="dm-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="assistant">Assistant</option>
              <option value="owner">Owner</option>
            </select>
          </div>
        </div>

        <div className="mt-5 border-t border-slate-100 pt-5">
          <p className="label-xs mb-1.5">Clinic ID</p>
          <input data-testid="admin-user-clinic-input" className="dm-input font-mono" placeholder="Optional" value={form.clinic_id} onChange={(e) => setForm({ ...form, clinic_id: e.target.value })} />
          <p className="mt-1.5 text-xs text-slate-500">Paste the exact clinic ID to attach this account to a clinic. Leave blank for an owner setting up a new clinic on first login.</p>
        </div>

        <button type="submit" disabled={creating} data-testid="admin-user-create-btn" className="dm-btn-primary mt-5 disabled:opacity-70">
          {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {creating ? "Creating…" : "Create account"}
        </button>
      </form>

      <div className="dm-panel overflow-hidden">
        <div className="dm-panel-head">
          <h2 className="font-head text-lg font-semibold text-slate-800">All accounts</h2>
          <span className="text-xs text-slate-500">{users.length} total</span>
        </div>
        {users.length === 0 ? (
          <div className="dm-empty m-4">No users yet.</div>
        ) : (
          <div className="max-h-[65vh] overflow-auto">
            <table className="dm-table w-full" data-testid="admin-users-table">
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Clinic</th>
                  <th>Status</th><th>Signed up</th><th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: "var(--dm-accent)" }}>
                          {initials(u.name)}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="truncate font-semibold text-slate-800">{u.name}</p>
                            {u.is_super_admin && (
                              <span className="dm-badge dm-badge-accent shrink-0">
                                <ShieldCheck size={11} /> Super admin
                              </span>
                            )}
                          </div>
                          <p className="truncate text-xs text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="capitalize text-slate-600">{u.role}</td>
                    <td className="text-slate-600">{u.clinic_name || "—"}</td>
                    <td>
                      <span className={`dm-badge ${u.status === "suspended" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                        {u.status === "suspended" ? "Suspended" : "Active"}
                      </span>
                    </td>
                    <td className="text-slate-600">{when(u.created_at)}</td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {u.status === "suspended" ? (
                          <IconAction tone="emerald" title="Reactivate account" disabled={u.is_super_admin} onClick={() => reactivate(u)}>
                            <PlayCircle size={16} />
                          </IconAction>
                        ) : (
                          <IconAction tone="amber" title="Pause account" disabled={u.is_super_admin} onClick={() => setConfirm({ user: u, action: "suspend" })}>
                            <PauseCircle size={16} />
                          </IconAction>
                        )}
                        <IconAction tone="red" title="Delete account" disabled={u.is_super_admin} onClick={() => setConfirm({ user: u, action: "delete" })}>
                          <Trash2 size={16} />
                        </IconAction>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && !working && setConfirm(null)}>
        <AlertDialogContent className="rounded-2xl border-slate-200 bg-white p-6 text-slate-900 shadow-xl sm:rounded-2xl" data-testid="admin-user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head text-lg font-semibold text-slate-900">{copy?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">{confirm && copy.body(confirm.user)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working} className="rounded-full border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="admin-user-confirm-action" disabled={working} onClick={(e) => { e.preventDefault(); runConfirmed(); }}
              className={`rounded-full px-4 py-2.5 text-sm font-semibold shadow-none ${copy?.actionClass}`}>
              {working ? <Loader2 size={15} className="animate-spin" /> : null} {copy?.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
